/**
 * stepNodeStyles.ts - Comprehensive styles for automation step nodes
 *
 * Purpose:
 *   Detailed styling for all automation step nodes including expanded/collapsed states,
 *   badges, indicators, and interactive elements.
 *
 * Usage:
 *   import { stepNodeStyles } from './stepNodeStyles';
 *   <div className={css(stepNodeStyles.expandedCard)} />
 *
 * States:
 *   - compactCard: Collapsed node state
 *   - expandedCard: Expanded node state with full content
 *   - selected: Visual highlight for selected nodes
 *   - invalid: Error state with validation warnings
 *
 * Features:
 *   - Contact badges and indicators
 *   - Parent case indicators (for switch steps)
 *   - Read-only mode badges
 *   - Switch step branch visualizations
 *
 * Dependencies:
 *   - colors.ts for color palette
 *   - styles.ts for cardStyle base
 *
 * Related:
 *   - AutomationStepNodeWrapper.tsx
 *   - nodes/styles.ts (base node styles)
 *   - SwitchStepRenderer.tsx
 */

import { StyleSheet } from 'aphrodite';
import {
	titles,
	charmGray,
	white,
	backgroundAlt,
	destructive,
	nodeBorderBlue,
	nodeLinkBlue,
	aceGray,
} from '../../../styles/colors';
import { Fonts } from '../../../styles/styles';
import { NODE_WIDTH, STEP_NODE_COMPACT_HEIGHT, STEP_NODE_EXPANDED_MIN_HEIGHT } from '../utils/layoutConstants';
import { baseStyles, nodeCardBase, shadows, transitions } from '../styles/baseStyles';

/**
 * Color Mapping (from hardcoded → theme):
 * #F3F4F6 → backgroundAlt
 * #6B7280 → charmGray
 * #DC2626 → destructive
 * #FFFFFF → white
 * #2C65F6 → nodeBorderBlue (handles, icon circle)
 * #2E67ED → nodeLinkBlue (links/editable text)
 * #C6CCD7 → aceGray (dividers)
 * Node card border colors: Dynamic per step type (see stepTypeColors.ts)
 */

/**
 * Base button style for node toggle buttons.
 * Extends transparentButton with node-specific sizing.
 */
const nodeButtonBase = {
	...baseStyles.transparentButton,
	padding: 8,
	minHeight: 44,
	minWidth: 44,
	':focus': baseStyles.focusRing,
};

export const styleSheet = StyleSheet.create({
	nodeContainer: {
		position: 'relative',
		// NOTE: Avoid `transition: all` on the node container.
		// React Flow relies on ResizeObserver to detect node height/width changes.
		// When height is animated, it can emit a stream of `dimensions` updates, which
		// then triggers repeated layout recalculations and visible flicker.
		fontFamily: Fonts.sora,
	},

	expandedCard: {
		...nodeCardBase,
		width: NODE_WIDTH,
		minHeight: STEP_NODE_EXPANDED_MIN_HEIGHT,
		maxHeight: 'none', // Allow node to grow as needed
		// Both axes must stay visible: CSS promotes a `visible` axis to `auto` whenever the other
		// axis clips, which would make the card a scroll container — and a clipping ancestor that
		// floating-ui shrinks popups (the advanced options selects) against.
		overflow: 'visible',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
	},

	// Inner content when wrapped in an outer container (no cardStyle since container provides it)
	expandedCardInner: {
		width: '100%',
		minHeight: STEP_NODE_EXPANDED_MIN_HEIGHT,
		maxHeight: 'none',
		overflow: 'visible',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
	},

	expandedContent: {
		padding: '16px 24px 24px',
		boxSizing: 'border-box',
		width: '100%',
		maxWidth: '100%',
		minWidth: 0,
		overflowWrap: 'anywhere',
		flex: 1,
	},

	expandedHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '14px 24px',
		borderBottom: `1px solid ${aceGray}`,
	},

	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		flex: 1,
		minWidth: 0,
	},

	headerTitle: {
		flex: 1,
		minWidth: 0,
		fontSize: 26,
		fontWeight: 600,
		lineHeight: '33px',
		color: titles,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},

	collapseButton: {
		...nodeButtonBase,
		borderRadius: 4,
		transition: transitions.background,
		':hover': {
			background: backgroundAlt,
		},
	},

	expandButton: {
		...nodeButtonBase,
	},

	compactCard: {
		...nodeCardBase,
		width: NODE_WIDTH,
		minHeight: STEP_NODE_COMPACT_HEIGHT,
		maxHeight: STEP_NODE_COMPACT_HEIGHT,
		padding: 20,
		cursor: 'pointer',
		boxSizing: 'border-box',
		overflow: 'hidden',
		transition: transitions.card,
		':hover': {
			boxShadow: shadows.cardHover,
		},
	},

	// Inner content when wrapped in an outer container (no cardStyle since container provides it)
	compactCardInner: {
		width: '100%',
		minHeight: STEP_NODE_COMPACT_HEIGHT,
		maxHeight: STEP_NODE_COMPACT_HEIGHT,
		padding: 20,
		cursor: 'pointer',
		boxSizing: 'border-box',
		overflow: 'hidden',
	},

	compactHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},

	compactTitle: {
		flex: 1,
		minWidth: 0,
		fontSize: 14,
		fontWeight: 600,
		color: titles,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},

	compactSchedule: {
		marginTop: 8,
		marginLeft: 4,
		fontSize: 12,
		color: charmGray,
	},

	selected: {
		boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.3)',
	},
	validationPath: {
		boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.18)',
		borderRadius: 12,
	},
	validationTarget: {
		boxShadow: '0 0 0 4px rgba(220, 38, 38, 0.18)',
		borderRadius: 12,
	},

	invalid: {
		borderColor: destructive,
	},

	handle: {
		// Make React Flow handles invisible - we use custom connector dots instead
		// The handle still functions for edge connections
		width: 1,
		height: 1,
		opacity: 0,
		background: 'transparent',
		border: 'none',
	},

	// Icon circle style matching Figma (42px × 42px with blue background)
	iconCircle: {
		width: 42,
		height: 42,
		borderRadius: '50%',
		background: nodeBorderBlue,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},

	// Step type indicator circle (colored circle on left side of node content)
	// Color is set dynamically via inline style based on step type
	// NOTE: This is now replaced by AutomationStepIcon (30x30px) in most cases
	stepIndicator: {
		width: 12,
		height: 12,
		borderRadius: '50%',
		flexShrink: 0,
	},

	// Header with step icon (flex container for icon + title)
	// Adjusted gap for 30px AutomationStepIcon
	headerWithIndicator: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		flex: 1,
	},

	// Compact card header with step icon
	// Adjusted gap for 30px AutomationStepIcon
	compactHeaderWithIndicator: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},

	parentCaseIndicator: {
		position: 'absolute',
		top: '-8px',
		left: '-8px',
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		color: white,
		borderRadius: 12,
		padding: '2px 8px',
		fontSize: 10,
		fontWeight: 600,
		boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
		zIndex: 20,
	},

	contactBadgeContainer: {
		position: 'absolute',
		top: '-8px',
		right: '-8px',
		display: 'flex',
		gap: 2,
		backgroundColor: '#fff',
		borderRadius: 12,
		padding: '4px 6px',
		boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
		zIndex: 10,
	},

	contactColorDot: {
		width: 12,
		height: 12,
		borderRadius: '50%',
		boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
	},

	contactOverflow: {
		fontSize: 10,
		fontWeight: 600,
		color: charmGray,
		marginLeft: 2,
		lineHeight: '12px',
	},

	contactCountBadge: {
		fontSize: 10,
		fontWeight: 600,
		color: charmGray,
		marginLeft: 2,
	},

	readOnlyBadge: {
		backgroundColor: charmGray,
		color: white,
		padding: '2px 8px',
		borderRadius: 4,
		fontSize: 10,
		fontWeight: 600,
		textTransform: 'uppercase' as const,
		letterSpacing: '0.5px',
	},

	switchCompactInfo: {
		marginTop: 8,
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 4,
	},

	switchBranchCount: {
		fontSize: 11,
		color: charmGray,
		fontWeight: 500,
	},

	switchCaseIndicators: {
		display: 'flex',
		flexDirection: 'row' as const,
		alignItems: 'center',
		gap: 4,
	},

	switchCaseDot: {
		width: 12,
		height: 12,
		borderRadius: '50%',
		flexShrink: 0,
	},

	switchOverflow: {
		fontSize: 10,
		color: charmGray,
		fontWeight: 600,
		marginLeft: 2,
	},

	// Typography styles matching Figma
	fieldLabel: {
		fontSize: 18,
		fontWeight: 700,
		lineHeight: '23px',
		color: titles,
	},

	fieldValue: {
		fontSize: 18,
		fontWeight: 400,
		lineHeight: '23px',
		color: titles,
	},

	fieldLabelRequired: {
		fontSize: 18,
		fontWeight: 400,
		lineHeight: '23px',
		color: '#DE2139', // alizarinCrimson for required
	},

	editableText: {
		fontSize: 18,
		fontWeight: 600,
		lineHeight: '23px',
		color: nodeLinkBlue,
		cursor: 'pointer',
		':hover': {
			textDecoration: 'underline',
		},
	},

	// Form field styles matching Figma
	inputField: {
		background: '#F9F9F9',
		border: '1px solid #E9EBEF',
		borderRadius: 8,
		padding: '10px 12px',
		fontSize: 14,
		lineHeight: '18px',
		color: titles,
		width: '100%',
		boxSizing: 'border-box' as const,
	},

	inputPlaceholder: {
		fontSize: 14,
		fontWeight: 400,
		lineHeight: '18px',
		color: aceGray,
	},

	dropdownField: {
		background: white,
		border: `1px solid ${aceGray}`,
		borderRadius: 8,
		padding: '10px 12px',
		fontSize: 16,
		lineHeight: '20px',
		color: titles,
		cursor: 'pointer',
	},

	// Divider line matching Figma
	divider: {
		width: '100%',
		height: 0,
		borderTop: `1px solid ${aceGray}`,
		margin: '16px 0',
	},

	// Three dots menu style
	menuDots: {
		display: 'flex',
		gap: 2,
	},

	menuDot: {
		width: 5.68,
		height: 5.68,
		borderRadius: '50%',
		background: '#D9D9D9',
	},
});

// Backwards compatible alias
export const stepNodeStyles = styleSheet;
